var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { z } from 'zod';
import { objectValues } from 'utils/TypescriptUtils';
import { FLAGS, FlagsSchema } from 'stores/FeatureFlagStore/types';
var DEFAULT_TIMEOUT_MS = 5000;
var KNOWN_FLAGS = objectValues(FLAGS);
// The `/flags?v=2` answer, as far as this module reads it.
var FlagsResponseSchema = z.object({
    flags: z.record(z.string(), z.object({
        enabled: z.boolean(),
        variant: z.string().nullable().optional()
    })),
    errorsWhileComputingFlags: z.boolean().optional(),
    quotaLimited: z.array(z.string()).optional()
});
// Called as the bare global, never bound: `globalThis` does not exist in the
// older WebViews and Fire OS browsers two of the bundles run in, and naming it
// there throws before the request's own protection starts.
var defaultFetch = function () { return (typeof fetch === 'function' ?
    function (url, init) { return fetch(url, init); } :
    undefined); };
/**
 * The `/flags?v=2` request body, exactly as it is sent.
 *
 * One key field, `token`: the one posthog-js itself sends. Sending `api_key`
 * as well makes PostHog refuse the whole request ("400 Failed to decode
 * request: invalid JSON"), which is how every page read every flag as off
 * from #554 until this was found on 2026-10-12. The body is built here, in
 * one place, so the unit test pins it and `npm run flags:probe` can post the
 * same bytes to the real endpoint.
 */
export var buildFlagRequestBody = function (context) {
    var token = context.token, distinctId = context.distinctId, accountId = context.accountId, environment = context.environment;
    return {
        token: token,
        distinct_id: distinctId,
        groups: { account: accountId },
        person_properties: { environment: environment },
        group_properties: { account: { $group_key: accountId, environment: environment } },
        // Evaluate only the flags this app reads. The field is the one
        // PostHog's own SDK core sends (`@posthog/core`
        // posthog-core-stateless: `flag_keys_to_evaluate`, a string array);
        // it is not on the public /flags reference page. Checked against the
        // live endpoint on 2026-10-12: it is honoured (six keys back, not the
        // project's twelve). The answer is read the same way either way.
        flag_keys_to_evaluate: KNOWN_FLAGS
    };
};
export var flagRequestUrl = function (host) { return "".concat(host.replace(/\/+$/, ''), "/flags/?v=2"); };
var refusalReported = false;
var reportNoAnswer = function (what) {
    if (refusalReported) {
        return;
    }
    refusalReported = true;
    var why = typeof what === 'number' ? "HTTP ".concat(what) : what;
    try {
        // eslint-disable-next-line no-console
        console.warn("Feature flags: the flag request got no answer (".concat(why, "). ") +
            'That request turned no flag on.');
    }
    catch (_a) {
        // A console that throws must not reach the caller.
    }
};
/** Test seam: the warning is once per page load, so a test resets it. */
export var resetFlagRefusalReportForTests = function () {
    refusalReported = false;
};
var evaluate = function (context, fetchImpl, signal) { return __awaiter(void 0, void 0, void 0, function () {
    var response, json, _a, parsed, candidate, answer;
    var _b;
    return __generator(this, function (_c) {
        switch (_c.label) {
            case 0: return [4 /*yield*/, fetchImpl(flagRequestUrl(context.host), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'omit',
                    signal: signal,
                    body: JSON.stringify(buildFlagRequestBody(context))
                })];
            case 1:
                response = _c.sent();
                if (!response.ok || response.status !== 200) {
                    reportNoAnswer(response.status);
                    return [2 /*return*/, null];
                }
                _c.label = 2;
            case 2:
                _c.trys.push([2, 4, , 5]);
                return [4 /*yield*/, response.json()];
            case 3:
                json = _c.sent();
                return [3 /*break*/, 5];
            case 4:
                _a = _c.sent();
                // A 200 whose body is not JSON. (A body cut off by the deadline also
                // lands here; the deadline has then already spoken.)
                reportNoAnswer('bad_answer');
                return [2 /*return*/, null];
            case 5:
                parsed = FlagsResponseSchema.safeParse(json);
                if (!parsed.success) {
                    reportNoAnswer('bad_answer');
                    return [2 /*return*/, null];
                }
                // A partial evaluation, or a spent flag quota, is not an answer.
                if (parsed.data.errorsWhileComputingFlags) {
                    reportNoAnswer('bad_answer');
                    return [2 /*return*/, null];
                }
                if ((_b = parsed.data.quotaLimited) === null || _b === void 0 ? void 0 : _b.includes('feature_flags')) {
                    reportNoAnswer('bad_answer');
                    return [2 /*return*/, null];
                }
                candidate = {};
                KNOWN_FLAGS.forEach(function (flag) {
                    var own = Object.prototype.hasOwnProperty.call(parsed.data.flags, flag);
                    var detail = own ? parsed.data.flags[flag] : undefined;
                    candidate[flag] = detail !== undefined && detail.enabled === true && detail.variant == null;
                });
                answer = FlagsSchema.safeParse(candidate);
                return [2 /*return*/, answer.success ? answer.data : null];
        }
    });
}); };
export var requestFeatureFlags = function (context, deps) {
    if (deps === void 0) { deps = {}; }
    // The default is resolved per call, and per missing field: passing only a
    // timeout still uses the page's fetch as it is now.
    var _a = deps.fetchImpl, fetchImpl = _a === void 0 ? defaultFetch() : _a, _b = deps.timeoutMs, timeoutMs = _b === void 0 ? DEFAULT_TIMEOUT_MS : _b;
    if (!fetchImpl ||
        !context.host ||
        !context.token ||
        !context.distinctId ||
        !context.accountId) {
        return Promise.resolve(null);
    }
    return new Promise(function (resolve) {
        var controller = typeof AbortController === 'function' ? new AbortController() : undefined;
        var settled = false;
        var timer;
        // Settles once. The deadline does not depend on fetch honouring the
        // abort signal: whatever arrives after it is discarded.
        var settle = function (flags) {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            resolve(flags);
        };
        timer = setTimeout(function () {
            if (!settled) {
                reportNoAnswer('timeout');
            }
            settle(null);
            controller === null || controller === void 0 ? void 0 : controller.abort();
        }, timeoutMs);
        // Network failure, abort, a synchronous throw from fetch, or a body
        // that is not JSON: no answer.
        Promise.resolve()
            .then(function () { return evaluate(context, fetchImpl, controller === null || controller === void 0 ? void 0 : controller.signal); })
            .then(settle, function () {
            // Not after the deadline: the abort it sends rejects the
            // fetch, and the deadline has already been named.
            if (!settled) {
                reportNoAnswer('network');
            }
            settle(null);
        });
    });
};
